import { useRef, useState } from "react"
import { Pencil } from "lucide-react"

import { KeyValue } from "@/components/KeyValue"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field, FieldLabel } from "@/components/ui/field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { UserTableRow } from "@/components/subject/userTableData"
import {
  addOtkRole,
  removeOtkRole,
  type OtkRole,
  type Role,
} from "@/lib/api/users"

export function EditUserDialog({
  disabled,
  onSave,
  roleRows,
  user,
}: {
  disabled?: boolean
  /** Called after a successful save (real role change, if any, already
   *  applied server-side) so the caller can update its local row state. */
  onSave: (roleId: Role) => void
  roleRows: OtkRole[]
  user: UserTableRow
}) {
  const closeWithPointer = useRef(false)
  const [open, setOpen] = useState(false)
  // The dialog exposes one "Роль в АРМ" picker. A user can hold more than
  // one role server-side (`OtkUser.roles`), but this UI treats the first as
  // "the" current role and, on save, replaces the whole set with the newly
  // picked one — same uniform-replace behaviour as before.
  const currentRoleId = user.roles[0] ?? null
  const [roleId, setRoleId] = useState<string>(
    currentRoleId ?? roleRows[0]?.id ?? ""
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasChanges = roleId !== (currentRoleId ?? roleRows[0]?.id ?? "")

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setRoleId(currentRoleId ?? roleRows[0]?.id ?? "")
      setError(null)
    }
    setOpen(nextOpen)
  }

  async function handleSave() {
    if (submitting || !hasChanges) return
    setSubmitting(true)
    setError(null)
    try {
      for (const role of user.roles) {
        await removeOtkRole(user.id, role)
      }
      await addOtkRole(user.id, roleId as Role)
      onSave(roleId as Role)
      setOpen(false)
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error
      setError(
        code === "Illegal Argument Exception"
          ? "Не вдалося оновити роль: користувача не знайдено."
          : "Не вдалося зберегти зміни. Спробуйте ще раз."
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button
          disabled={disabled}
          size="sm"
          title={
            disabled
              ? "Суб'єкт виключений з реєстру — доступний лише перегляд"
              : undefined
          }
          variant="outline"
        >
          <Pencil data-icon="inline-start" />
          Редагувати
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          if (closeWithPointer.current) event.preventDefault()
        }}
        onKeyDownCapture={() => {
          closeWithPointer.current = false
        }}
        onPointerDownCapture={() => {
          closeWithPointer.current = true
        }}
      >
        <DialogHeader>
          <DialogTitle>Редагування користувача</DialogTitle>
        </DialogHeader>
        <KeyValue
          className="flex-wrap"
          label="E-mail:"
          value={<span className="break-all">{user.email}</span>}
        />
        <KeyValue
          className="flex-wrap"
          label="Відповідальна особа:"
          value={user.responsiblePerson}
        />
        <Field>
          <FieldLabel>Роль в АРМ</FieldLabel>
          <Select onValueChange={setRoleId} value={roleId}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleRows.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button
            disabled={submitting}
            onClick={() => setOpen(false)}
            variant="outline"
          >
            Відмінити
          </Button>
          <Button disabled={!hasChanges || submitting} onClick={handleSave}>
            {submitting ? "Збереження…" : "Зберегти"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
